import type { MouseEvent as ReactMouseEvent } from 'react';
import { useMemo } from 'react';

import { DockedSidePanel } from '@/components/reasoning/DockedSidePanel';
import { DynamicViewSidebar } from '@/components/reasoning/DynamicViewSidebar';
import { ReasoningSidebar } from '@/components/reasoning/ReasoningSidebar';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import {
  INLINE_PANEL_MAX_WIDTH,
  INLINE_PANEL_MIN_WIDTH,
} from '@/hooks/useCanHostInlinePanel';
import type { SurfaceDismissContextValue } from '@/lib/base/overlays/surface-dismiss-context';
import { SurfaceDismissContext } from '@/lib/base/overlays/surface-dismiss-context';
import { EdgeResizeHandle } from '@/lib/base/resize-handle/edge-resize-handle';

import type { ChatChain } from './types';

interface ChatSidePanelProps {
  orgId: string;
  conversationId: number;
  isCreator: boolean;
  dynamicViewId: number | null;
  /** The chain whose reasoning is shown; null when the panel shows a view. */
  reasoningChain: ChatChain | null;
  showReasoning: boolean;
  /** Panel width in px — the shared slideout width, like every docked pane. */
  width: number;
  isResizing: boolean;
  onResizeStart: (event: ReactMouseEvent) => void;
  onKeyboardResize: (deltaPx: number) => void;
  onClose: () => void;
  onOpenDynamicViewSidebar: (viewId: number) => void;
}

/**
 * The right-hand column showing a dynamic view or a chain's reasoning.
 * Full-screen on mobile; on desktop a DockedSidePanel — the same docked
 * flex-sibling container (and shared px slideout width) as the docked
 * proposal pane, so the chat's side surfaces cannot drift apart.
 *
 * The chrome contract: this host owns dismissal and publishes it through
 * SurfaceDismissContext, so panel bodies built on SlideoutMenu chrome
 * (ReasoningSidebar) render the standard close button in every state.
 */
export function ChatSidePanel({
  orgId,
  conversationId,
  isCreator,
  dynamicViewId,
  reasoningChain,
  showReasoning,
  width,
  isResizing,
  onResizeStart,
  onKeyboardResize,
  onClose,
  onOpenDynamicViewSidebar,
}: ChatSidePanelProps) {
  const isDesktop = useBreakpoint('lg');

  const dismiss = useMemo<SurfaceDismissContextValue>(
    () => ({
      close: onClose,
      back: undefined,
      isEntering: false,
      isExiting: false,
    }),
    [onClose]
  );

  const body = (
    <SurfaceDismissContext value={dismiss}>
      {!!dynamicViewId && (
        <DynamicViewSidebar
          conversationId={conversationId}
          orgId={orgId}
          viewId={dynamicViewId}
          onClose={onClose}
          isCreator={isCreator}
        />
      )}
      {showReasoning && (
        <ReasoningSidebar
          chain={reasoningChain}
          onOpenDynamicViewSidebar={onOpenDynamicViewSidebar}
        />
      )}
    </SurfaceDismissContext>
  );

  if (!isDesktop) {
    // Mobile: the panel is the full-screen surface (the chat column hides —
    // see ChatMainColumn). DockedSidePanel is lg-only, so a straight swap
    // would make "Show reasoning" do nothing on phones.
    return (
      <div className='fixed inset-0 z-50 flex flex-col bg-bg-primary'>
        {body}
      </div>
    );
  }

  return (
    <DockedSidePanel
      width={width}
      data-component='chat-side-panel'
      resizeHandle={
        <EdgeResizeHandle
          edge='left'
          onResizeStart={onResizeStart}
          onKeyboardResize={onKeyboardResize}
          width={width}
          minWidth={INLINE_PANEL_MIN_WIDTH}
          maxWidth={INLINE_PANEL_MAX_WIDTH}
          isResizing={isResizing}
          aria-label='Resize sidebar panel. Use left and right arrow keys to resize.'
        />
      }
    >
      {body}
    </DockedSidePanel>
  );
}
